import { useState } from "react";
import { CalendarDays, X } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CAL_CLASSES } from "@/components/tracker/DateTimePicker";
import { FIELD, MENU, Chevron } from "@/components/tracker/FormFields";

const toIso = (d) => d.toLocaleDateString("en-CA");
const fromIso = (s) => new Date(`${s}T12:00:00`);
const SMALL = "flex h-9 w-full items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-3 text-left text-xs text-white outline-none transition-colors duration-150 hover:border-white/20 data-[state=open]:border-[#00FF66]/60";

// The app's one date picker (YYYY-MM-DD string): styled field + green calendar pop-over.
export function DateField({ value, onChange, placeholder = "Pick a date", testid, allowPast = false, min, size = "md", short = false, clearable = true, disabled = false }) {
  const [open, setOpen] = useState(false);
  const sm = size === "sm";
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const floor = min ? fromIso(min) : allowPast ? null : today;
  const sameYear = value && fromIso(value).getFullYear() === new Date().getFullYear();
  const shortFmt = sameYear ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" };
  const label = value ? fromIso(value).toLocaleDateString([], sm || short ? shortFmt : { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : placeholder;
  return (
    <Popover open={open} onOpenChange={(v) => !disabled && setOpen(v)}>
      <div className="relative min-w-0 flex-1">
        <PopoverTrigger asChild>
          <button type="button" disabled={disabled} className={`${sm ? `${SMALL} pr-8` : `${FIELD} pr-11`} disabled:opacity-50`} data-testid={testid}>
            <CalendarDays className={`${sm ? "h-3.5 w-3.5" : "h-4 w-4"} shrink-0 ${value ? "text-[#00FF66]" : "text-white/35"}`} />
            <span className={`flex-1 truncate ${value ? "font-semibold" : "text-white/35"}`}>{label}</span>
          </button>
        </PopoverTrigger>
        <span className={`absolute top-1/2 flex -translate-y-1/2 ${sm ? "right-2.5" : "right-[17px]"}`}>
          {value && clearable ? (
            <button type="button" onClick={() => onChange("")} className="-m-1 flex rounded-md p-1 text-white/40 transition-colors hover:bg-white/10 hover:text-white" aria-label="Clear date" data-testid={`${testid}-clear`}>
              <X className={sm ? "h-3.5 w-3.5" : "h-4 w-4"} />
            </button>
          ) : <span className="pointer-events-none flex"><Chevron /></span>}
        </span>
      </div>
      <PopoverContent align="start" sideOffset={8} className={`${MENU} w-auto p-4`} data-testid={`${testid}-calendar`}>
        <Calendar
          mode="single"
          selected={value ? fromIso(value) : undefined}
          defaultMonth={value ? fromIso(value) : min ? fromIso(min) : undefined}
          onSelect={(d) => { if (d) { onChange(toIso(d)); setOpen(false); } }}
          disabled={floor ? { before: floor } : undefined}
          className="p-0"
          classNames={CAL_CLASSES}
        />
      </PopoverContent>
    </Popover>
  );
}
